import { useEffect, useState } from "react";
import { omit } from "lodash";

import { useThrottledCallback } from "./useThrottledCallback";

const InfinitelyScrollableContainer = ({
  onBottomReached = () => {},
  offset = 100,
  ...props
}) => {
  const { children } = props;

  const passthroughProps = omit(props, [
    "onBottomReached",
    "offset",
    "scrollableContainer"
  ]);

  const bindsWindowScroll = !props.scrollableContainer;
  const scrollableContainer = bindsWindowScroll ? window : props.scrollableContainer;
  const [container, setContainer] = useState();

  // We throttle the scroll handling callback to ensure that it's not called more than
  // three times a second as the next page data loading doesn't have to be extremely
  // responsive, and this allows to lighten scroll events processing, as there can be
  // many InfinitelyScrollableContainer components on the page at the same time, for
  // example in contributions with navigation and comments lists.
  const handleContainerScrolled = useThrottledCallback(
    () => {
      // Wait until the container ref has been set by react before trying to handle the
      // scroll, or never do anything if no callback is provided.
      if (!container || !onBottomReached) return;

      // Fetch total height of the scrollable container to know the window we're looking
      // at.
      const clientHeight = bindsWindowScroll
        ? document.documentElement.clientHeight || window.innerHeight
        : scrollableContainer.clientHeight;

      // Check the current scroll offset of our container.
      const scrollTop = bindsWindowScroll
        ? window.pageYOffset
        : scrollableContainer.scrollTop;

      // When bound to the window scroll, we calculate the top offset of the container
      // by summing the current page scroll offset and the bounding client rect's top
      // property. This allows us to get an absolute offset top value even for elements
      // that are inside a relatively positionned container, like the comments panels.
      const containerOffsetTop = bindsWindowScroll
        ? scrollTop + container.getBoundingClientRect().top
        : container.offsetTop;

      // Build the value that we must reach before we trigger the `onBottomReached`
      // callback function.
      const scrollTopTarget = containerOffsetTop + container.clientHeight - offset;

      // If the bottom of the window is scrolled to the bottom of our container,
      // minus the defined offset, we consider the bottom to be reached and call
      // the provided `onBottomReached` callback
      //
      const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollTopTarget;

      if (scrolledToBottom) onBottomReached();
    },
    [offset, onBottomReached, bindsWindowScroll, scrollableContainer, container],
    300,
    { leading: false }
  );

  useEffect(() => {
    scrollableContainer.addEventListener("scroll", handleContainerScrolled);

    return () => {
      scrollableContainer.removeEventListener("scroll", handleContainerScrolled);
    };
  }, [scrollableContainer, handleContainerScrolled]);

  return (
    <div {...passthroughProps} ref={setContainer}>
      {children}
    </div>
  );
};

export default InfinitelyScrollableContainer;
